import { labels, currentResource } from "../../util/utils";
import { ServiceDataModel } from "../../services/models/Models";
import { EntityConfiguration } from "../../configurations/configuration";
import _ = require("lodash");
import {
  CellIndexMapping,
  Constants,
  DocumentColumn,
  CommonColumn,
  ExcelColorValue,
  UploadStatus,
  ColumnType,
  ExcelFormatting,
} from "../../util/UIConstants";
import Utility from "../../util/utilityFunctions";
import { Environment } from "../../util";
import { ExcelHelper } from "../ExcelHelper";
import hash = require("object-hash");

type masterDataNames = "YesNoList";
type masterDataMetaDataValues = {
  keyColumn: string;
  keyAddressRange: string;
  value1Column: string;
  value1AddressRange: string;
  value2Column: string;
  value2AddressRange: string;
};
type masterDataDropDownMetaDataAddrRanges = Record<masterDataNames, masterDataMetaDataValues>;

export class ExcelUtilityHelper {
  /**
   * Marks the updated row range yellow
   * @param context - The Context
   * @param sheet - The Sheet
   * @param firstUpdatedRowIndex - First updated row index
   * @param lastUpdatedRowIndex
   * @param color
   */
  public static async doMarkForUpdateRangeFormatChanges(
    context: Excel.RequestContext,
    sheet: Excel.Worksheet,
    firstUpdatedRowIndex: number,
    lastUpdatedRowIndex: number,
    beginningColumn: string,
    endingDataColumnCellName: string,
    configuration,
    color: string = ExcelColorValue.Yellow
  ) {
    await Excel.run(async (context) => {
      const sheetId = configuration?.settings?.sheetId;
      let activeSheet = context.workbook.worksheets.getItem(sheetId);
      let wholeRowRange = activeSheet.getRange(
        `${beginningColumn}${firstUpdatedRowIndex}:${endingDataColumnCellName}${lastUpdatedRowIndex}`
      );
      if (_.isEmpty(color)) {
        wholeRowRange.format.fill.clear();
        // to apply default row colors
        for (let i = 0; i < configuration.columns.length; i++) {
          const column = configuration.columns[i];
          const cellName = CellIndexMapping[column.CellDisplayIndex];
          let selectedRange = activeSheet.getRange(
            `${cellName}${firstUpdatedRowIndex}:${cellName}${lastUpdatedRowIndex}`
          );
          if (column.CellFormat.RowsFillColor) {
            selectedRange.format.fill.color = column.CellFormat.RowsFillColor;
          }
          if (column.CellFormat.lockedCondition) {
            selectedRange.load("format/protection/locked");
            await context.sync();
            let isAnyColumnProtected = selectedRange.format.protection.locked;
            if (isAnyColumnProtected) {
              selectedRange.format.fill.color = ExcelColorValue.GrayColor;
            }
          }
        }

        // if (configuration?.uiExcelInterface?.applyFormattingOnCellChange) {
        //   await configuration?.uiExcelInterface?.applyFormattingOnCellChange(
        //     context,
        //     sheet,
        //     firstUpdatedRowIndex,
        //     lastUpdatedRowIndex,
        //     configuration
        //   );
        // }
      } else {
        wholeRowRange.format.fill.color = color;
      }

      await context.sync(); // Ensure all changes are applied
    });
  }

  //Apply all type of formatting while populating data in excel.
  public static defaultFormatRequestTableColors(
    activeSheet: Excel.Worksheet,
    beginDataRowIndex: number,
    endDataRowIndex: number,
    configuration: EntityConfiguration
  ) {
    const tableRowHeaderIndex = 2;

    configuration.columns.forEach((column) => {
      const cellName = CellIndexMapping[column.CellDisplayIndex];

      const generalRange = activeSheet.getRange(`${cellName}${tableRowHeaderIndex}:${cellName}${endDataRowIndex}`);

      generalRange.format.verticalAlignment = Excel.VerticalAlignment.top;
      //generalRange.format.font.name = "Calibri";

      if (column.CellFormat.HeaderFillColor) {
        activeSheet.getRange(`${cellName}${tableRowHeaderIndex}`).format.fill.color = column.CellFormat.HeaderFillColor;
      }
      if (column.CellFormat.HeaderFontColor) {
        activeSheet.getRange(`${cellName}${tableRowHeaderIndex}`).format.font.color = column.CellFormat.HeaderFontColor;
      }

      // For applying string format to column for complete data rows in excel
      if (column.CellFormat.disableDefaultFormatting) {
        let taskNameRange = activeSheet.getRange(`${cellName}${beginDataRowIndex}:${cellName}${endDataRowIndex}`);
        taskNameRange.numberFormat = [["@"]];
      }

      if (column.CellFormat.RowsFillColor) {
        activeSheet.getRange(`${cellName}${beginDataRowIndex}`).format.fill.color = column.CellFormat.RowsFillColor;
      }

      if (column.CellFormat.RowsFontColor) {
        activeSheet.getRange(`${cellName}${beginDataRowIndex}`).format.font.color = column.CellFormat.RowsFontColor;
      }

      if (column.CellFormat.locked == false) {
        // to make data cells editable but header cell should still be protected.
        activeSheet.getRange(`${cellName}${beginDataRowIndex}`).format.protection.locked = column.CellFormat.locked;
      }
      if (column.CellFormat.horizontalAlignment) {
        activeSheet.getRange(
          `${cellName}${tableRowHeaderIndex}:${cellName}${endDataRowIndex}`
        ).format.horizontalAlignment = column.CellFormat.horizontalAlignment;
      }
      if (column.CellFormat.columnHidden) {
        activeSheet.getRange(`${cellName}:${cellName}`).columnHidden = column.CellFormat.columnHidden;
      }

      if (column.CellFormat.adjustColumnWidth) {
        activeSheet.getRange(`${cellName}:${cellName}`).format.columnWidth = 553.54;
      }

      if (column.CellFormat.dateFormat) {
        let taskNameRange = activeSheet.getRange(`${cellName}${beginDataRowIndex}:${cellName}${endDataRowIndex}`);
        //taskNameRange.numberFormat = [["YYYY-MM-DD"]];
        taskNameRange.numberFormat = [["@"]];
      }

      if (column.CellFormat.requiredColumnWidth) {
        activeSheet.getRange(`${cellName}:${cellName}`).format.columnWidth = column.CellFormat.requiredColumnWidth;
      }
    });

    // Don't wrap text in all cells
    activeSheet.getUsedRange().format.wrapText = false;

    configuration.columns
      .filter((f) => f?.CellFormat?.wrapText)
      .forEach((column) => {
        const cellName = CellIndexMapping[column.CellDisplayIndex];
        activeSheet.getRange(`${cellName}:${cellName}`).format.wrapText = true;
      });

    activeSheet.freezePanes.freezeRows(CommonColumn.NoOfFreezeRows);
  }

  /**
   * Loads the existing table, and active worksheet
   * @param context - The table context
   * @param engagementId - Engagement Id.
   * @returns Promise resolving to table name, existing table, sheet
   */
  public static async loadRequestsWorksheetTable(
    context: Excel.RequestContext,
    engagementId: string,
    activeSheetId: string
  ): Promise<{
    tableName: string;
    existingRequestsTable: Excel.Table;
    sheet: Excel.Worksheet;
  }> {
    let sheet = context.workbook.worksheets.getItem(activeSheetId);
    sheet.load("name");
    const tableName: string = `${Constants.RequestTablePrefix}${engagementId}`;
    let existingRequestsTable: Excel.Table = sheet.tables.getItemOrNullObject(tableName);
    await ExcelHelper.tryCatchContextSync(context, `get ${tableName}`);
    return { tableName, existingRequestsTable, sheet };
  }

  //To find modified rows and apply required formatting.
  //Changing Mark for update to Yes.
  public static async doRelativeCellUpdatesAndMarkDirty(
    firstUpdatedRowIndex: number,
    lastUpdatedRowIndex: number,
    sheet: Excel.Worksheet,
    context: Excel.RequestContext,
    markForUpdateCellName: string,
    configuration: EntityConfiguration,
    uploadStatusCellName: string,
    uploadStatusIdCellName: string,
    endingDataColumnCellName: string
  ) {
    let updatedRowIndexes: Array<number> = new Array<number>();
    for (let i: number = firstUpdatedRowIndex; i <= lastUpdatedRowIndex; i++) {
      this.makeMarkForUpdateCellDirty(i, sheet, markForUpdateCellName);
      updatedRowIndexes.push(i);

      this.doUploadStatusValueAndFormatChanges(
        sheet,
        i,
        UploadStatus.Pending,
        uploadStatusCellName,
        uploadStatusIdCellName
      );
    }
    let hashModifiedRowIndexes = await this.updateHashValues(updatedRowIndexes, sheet, context, configuration);
    if (!_.isEmpty(hashModifiedRowIndexes)) {
      await ExcelUtilityHelper.doMarkForUpdateRangeFormatChanges(
        context,
        sheet,
        firstUpdatedRowIndex,
        lastUpdatedRowIndex,
        CommonColumn.BeginningColumn,
        endingDataColumnCellName,
        configuration
      );
      await ExcelHelper.tryCatchContextSync(
        context,
        `mark row yellow dirty fromRow: ${firstUpdatedRowIndex} : toRow: ${lastUpdatedRowIndex}`
      );
    }
  }

  public static getCellLabel(configuration, labelKey, column) {
    if (column.CellHeaderResourceName) {
      const label = currentResource[column.CellHeaderResourceName][labelKey];
      return label;
    }
    const label = labels[labelKey];
    return label ? label : labelKey;
  }

  public static async populateExcelRowHeader(context: Excel.RequestContext, configuration, sheet: Excel.Worksheet) {
    let rowHeaderData = new Array<any>();
    configuration?.entityConfiguration?.rowheader?.forEach((header) => {
      rowHeaderData.push({
        CellHeader: this.getCellLabel(configuration, header.CellHeader, header),
        DisplayRange: header.DisplayRange,
        HeaderFillColor: header.CellFormat?.HeaderFillColor ? header.CellFormat.HeaderFillColor : null,
      });
    });

    for (const header of rowHeaderData) {
      for (const range of header.DisplayRange) {
        //A1:B1
        let cellRange = sheet.getRange(range);
        await context.sync();
        cellRange.load(["columnCount"]);
        await context.sync();
        const numberOfCells = cellRange.columnCount; // Calculate the number of cells
        let headerValues = new Array(numberOfCells).fill("");
        headerValues[0] = header.CellHeader;
        cellRange.values = [headerValues];
        await ExcelHelper.tryCatchContextSync(context, `Adding Value`);
        cellRange.merge(true);
        cellRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
        cellRange.format.verticalAlignment = Excel.VerticalAlignment.top;
        //cellRange.format.font.name = "Calibri";
        cellRange.format.font.bold = true;
        if (!_.isNull(header.HeaderFillColor)) {
          cellRange.format.fill.color = header.HeaderFillColor;
        }

        // Set borders to ensure they are visible after merging and applying fill color
        // let excelEdge : ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight"] = ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight"];
        // excelEdge.forEach(edge => {
        //   cellRange.format.borders.getItem(edge).style = Excel.BorderLineStyle.continuous;
        //   cellRange.format.borders.getItem(edge).color = "black"; // Set border color as needed
        // });

        cellRange.format.autofitRows();
        cellRange.format.autofitColumns();
        await context.sync();
      }
    }
  }

  //Changing Mark for update to Yes.
  public static makeMarkForUpdateCellDirty(
    addressRowIndex: number,
    sheet: Excel.Worksheet,
    markForUpdateCellName: string
  ) {
    let markForUpdateCellRange = sheet.getRange(
      `${markForUpdateCellName}${addressRowIndex}:${markForUpdateCellName}${addressRowIndex}`
    );
    markForUpdateCellRange.values = [[Constants.MarkForUpdateYes]];
  }

  //To change status for records as per requirements.
  public static doUploadStatusValueAndFormatChanges(
    sheet: Excel.Worksheet,
    rowIndex: number,
    status: UploadStatus | string,
    uploadStatusCellName: string,
    uploadStatusIdCellName: string,
    markForUpdateCellName: string = ""
  ) {
    //const statusLabel = status ? (currentResource.uploadStatusLabels[status] ? currentResource.uploadStatusLabels[status] : status) : status;
    const statusLabel =
      status && currentResource.uploadStatusLabels[status] ? currentResource.uploadStatusLabels[status] : status;
    let uploadStatusCellRange = sheet.getRange(`${uploadStatusCellName}${rowIndex}:${uploadStatusCellName}${rowIndex}`);
    let uploadStatusIdCellRange = sheet.getRange(
      `${uploadStatusIdCellName}${rowIndex}:${uploadStatusIdCellName}${rowIndex}`
    );
    uploadStatusCellRange.format.protection.locked = false;
    uploadStatusIdCellRange.format.protection.locked = false;
    uploadStatusCellRange.values = [[`${statusLabel}`]];
    uploadStatusIdCellRange.values = [[status]];
    uploadStatusCellRange.format.protection.locked = true;
    uploadStatusIdCellRange.format.protection.locked = true;

    if (markForUpdateCellName) {
      let uploadMarkForUpdate = sheet.getRange(
        `${markForUpdateCellName}${rowIndex}:${markForUpdateCellName}${rowIndex}`
      );
      uploadMarkForUpdate.values = [[Constants.MarkForUpdateNo]];
    }
  }

  /**
   * Updates hash values only when editable values changed.
   * @param addressRowIndexes Non-Zero based Excel Address Index. i.e. A1 means 1st column 1st row
   * @param sheet current sheet
   * @param context request context
   * @returns Zero-based row index. i.e. A5 means 4 rowIndex
   */
  public static async updateHashValues(
    addressRowIndexes: Array<number>,
    sheet: Excel.Worksheet,
    context: Excel.RequestContext,
    configuration: EntityConfiguration
  ): Promise<Array<number>> {
    let hashModifiedRowIndexes: Array<number> = new Array<number>();
    if (!_.isEmpty(addressRowIndexes)) {
      let dataToUpdateBackToCanvas = Utility.getHashValueColumnsToUpdate(configuration.columns, []); // can be more then one
      let editableFieldRanges: Array<any> = new Array<any>();
      let hashColumn = configuration.columns.find((cl) => cl.CellId == ColumnType.HashValue); // can be one
      let hashCellName = "";
      if (hashColumn) {
        hashCellName = CellIndexMapping[hashColumn.CellDisplayIndex];
      }
      _.forEach(addressRowIndexes, (i) => {
        let objectCell = {};
        dataToUpdateBackToCanvas.forEach((column) => {
          const cellName = CellIndexMapping[column.CellDisplayIndex];
          let updatedData = sheet.getRange(`${cellName}${i}:${cellName}${i}`);
          updatedData.load("text");
          updatedData.load("rowIndex");
          objectCell[column.CellId] = updatedData;
        });

        if (hashColumn) {
          hashCellName = CellIndexMapping[hashColumn.CellDisplayIndex];
          let hashValueCellRange = sheet.getRange(`${hashCellName}${i}:${hashCellName}${i}`);
          hashValueCellRange.load("values");
          hashValueCellRange.load("text");
          hashValueCellRange.load("address");
          hashValueCellRange.load("rowIndex");
          objectCell[hashColumn.CellId] = hashValueCellRange;
        }

        editableFieldRanges.push(objectCell);
      });

      // for performance reasons first load all the text, then perform updates
      await ExcelHelper.tryCatchContextSync(context, `load selected edit cell ranges for hash update`);
      _.forEach(editableFieldRanges, (editableFieldRange) => {
        let updatedData = "";
        dataToUpdateBackToCanvas.forEach((column) => {
          updatedData += editableFieldRange[column.CellId].text.at(0)[0];
        });

        const newHash: string = hash(updatedData);

        let hashValueCellRange = editableFieldRange?.HashValue;
        let existingHash = "";
        if (hashValueCellRange) {
          existingHash = hashValueCellRange.text.at(0)[0];

          // A table built for an entity that returned no records keeps one blank placeholder row whose
          // hash cell was never seeded. Hashing it would flag a non-existent record as edited and write
          // to locked cells on the protected input sheet, which fails.
          if (!updatedData && !existingHash) {
            return;
          }

          if (existingHash !== newHash) {
            hashValueCellRange.format.protection.locked = false;
            hashValueCellRange.values = [[newHash]];
            hashValueCellRange.format.protection.locked = true;
            hashModifiedRowIndexes.push(hashValueCellRange.rowIndex);
          }
        }
      });
    }

    return hashModifiedRowIndexes;
  }

  /**
   * Gets the non-zero-indexes of the Table body row i.e. excluded header row
   * @param context context
   * @param existingRequestsTable table
   * @returns updatedRowIndexes [non-zero-indexes of the Table body row],
   * notUpdatedRowIndexes [non-zero-indexes of the Table body row] &
   * totalDataRowCount [non-zero-based-header-included address row count. i.e. A5 = 5th row in the excel]
   */

  //Return updated, Not updated row indexes in offline mode and on save to EY Canvas.
  public static async getTableBodyRowNumbersMarkedForUpdate(
    context: Excel.RequestContext,
    existingRequestsTable: Excel.Table,
    configuration: EntityConfiguration
  ): Promise<{
    updatedRowIndexes: number[];
    notUpdatedRowIndexes: number[];
    totalDataRowCount: number;
  }> {
    let updatedRowIndexes = null;
    let notUpdatedRowIndexes = null;

    // Get data from a single column.
    let markForUpdateColumn = configuration.columns.find((x) => x.CellId == ColumnType.MarkForUpdate);
    let statusColumn = configuration.columns.find((x) => x.CellId == ColumnType.UploadStatus);
    let markUpdateColumnRange = existingRequestsTable.columns
      .getItemAt(markForUpdateColumn.CellDisplayIndex)
      .getDataBodyRange()
      .load("values");
    let uploadedColumnRange = existingRequestsTable.columns
      .getItemAt(statusColumn.CellDisplayIndex)
      .getDataBodyRange()
      .load("values");
    markUpdateColumnRange.load("values, rowCount");
    uploadedColumnRange.load("values");
    await ExcelHelper.tryCatchContextSync(context, "updated address ranges");

    let markUpdateColumnValues: string[][] = markUpdateColumnRange.values;
    let uploadedColumnValues: string[][] = uploadedColumnRange.values;

    const totalDataRowCount = markUpdateColumnRange.rowCount;
    let flattenedUpdateValues: string[] = markUpdateColumnValues.flatMap((item) => item);
    let flattenedUploadValues: string[] = uploadedColumnValues.flatMap((item) => item);
    updatedRowIndexes = [];
    notUpdatedRowIndexes = [];

    flattenedUpdateValues.map((item: string, index: number) => {
      if (
        String(item).toLowerCase() == Constants.MarkForUpdateYes.toLowerCase() &&
        String(flattenedUploadValues[index]).toLowerCase() !== UploadStatus.Uploaded.toString().toLowerCase()
      ) {
        updatedRowIndexes.push(index);
      } else {
        notUpdatedRowIndexes.push(index);
      }
    });

    if (updatedRowIndexes.length === 0) {
      updatedRowIndexes = null;
    }

    if (notUpdatedRowIndexes.length === 0) {
      notUpdatedRowIndexes = null;
    }

    return { updatedRowIndexes, notUpdatedRowIndexes, totalDataRowCount };
  }

  /**
   * Updates values for upload status column for updated row ranges in offline mode
   * and renders its
   * @param uploadStatusRanges - range
   * @param status - status enum
   */
  public static updateStatusColumn(
    uploadStatusRanges: Excel.Range[],
    uploadStatusIdRanges: Excel.Range[],
    status: UploadStatus
  ) {
    let rowIndex = 0;
    uploadStatusIdRanges.forEach((statusRange) => {
      const statusValue: string = statusRange.values.at(0)?.[CommonColumn.status]?.toString();
      switch (statusValue) {
        case UploadStatus.Pending.toString():
          statusRange.format.protection.locked = false;
          uploadStatusRanges[rowIndex].format.protection.locked = false;
          statusRange.values = [[UploadStatus.Pending]];
          uploadStatusRanges[rowIndex].values = [[currentResource.uploadStatusLabels[UploadStatus.Pending]]];
          statusRange.format.protection.locked = true;
          uploadStatusRanges[rowIndex].format.protection.locked = true;
          break;
        case UploadStatus.Uploaded.toString():
        case UploadStatus.Downloaded.toString():
        case "":
        case null:
        case undefined:
          statusRange.format.protection.locked = false;
          uploadStatusRanges[rowIndex].format.protection.locked = false;
          statusRange.values = [[status]];
          uploadStatusRanges[rowIndex].values = [[currentResource.uploadStatusLabels[status]]];
          statusRange.format.protection.locked = true;
          uploadStatusRanges[rowIndex].format.protection.locked = true;
          break;
        default:
          break;
      }
      rowIndex++;
    });
  }

  public static formatData(data, subHeadersDetails) {
    let finalValues = [];
    data.forEach((item) => {
      let values = [];
      subHeadersDetails.forEach((subHeader) => {
        values.push(subHeader.getCellData(item));
      });
      finalValues.push(values);
    });
    return finalValues;
  }

  /**
   * Create master data sheet
   * @param rowsDataArray
   * @param requestsModel
   * @returns master data record
   */
  // rendering or loading data on reference sheet
  public static async masterDataRender(
    rowsDataArray: Array<any>,
    rowsDataModel: ServiceDataModel,
    activeSheetId: string,
    configuration: any
  ): Promise<[masterDataDropDownMetaDataAddrRanges, any[]]> {
    let masterDataAddressRanges: masterDataDropDownMetaDataAddrRanges;
    let additionalReferenceSheetBindingInformation = [];
    let additionalInstructionSheetBindingInformation = [];
    if (rowsDataModel && rowsDataArray && rowsDataArray.length > 0) {
      await ExcelHelper.tryCatchRunAsync(async (context) => {
        context.runtime.enableEvents = false;
        await ExcelHelper.tryCatchContextSync(context, `disable event`);
        const masterSheetName = `${Constants.MasterWorkSheetPrefix}`;
        const masterTblName: string = `${Constants.MasterTablePrefix}${rowsDataModel.engagementId}`;

        //instruction sheet code
        let instructionSheetName = `${Constants.InstructionSheetName}`;

        let currentSheet: Excel.Worksheet;
        currentSheet = context.workbook.worksheets.getItem(activeSheetId);

        let worksheets: Excel.WorksheetCollection = context.workbook.worksheets;
        worksheets.load("items");

        let masterSheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(masterSheetName);
        await ExcelHelper.tryCatchContextSync(context, `load the sheet ${masterSheetName}`);
        if (!masterSheet.isNullObject) {
          masterSheet.delete();
          await ExcelHelper.tryCatchContextSync(context, `delete existing worksheet ${masterSheetName}`);
          masterSheet = context.workbook.worksheets.getItemOrNullObject(masterSheetName);
          await ExcelHelper.tryCatchContextSync(context, `reload the sheet after delete ${masterSheetName}`);
        }
        if (configuration?.referenceSheetColumns?.length) {
          // Move the newly created master sheet behind the input sheet
          const inputSheet = context.workbook.worksheets.getItem(activeSheetId);
          inputSheet.load("position");
          await context.sync();

          masterSheet = context.workbook.worksheets.add(masterSheetName);

          masterSheet.visibility = Excel.SheetVisibility.hidden;

          // Mastersheet will take one position ahead of input sheet and input sheet will remain where it is right now.
          masterSheet.position = inputSheet.position + 1;

          await ExcelHelper.tryCatchContextSync(context, `created master worksheet ${masterSheetName}`);

          //masterSheet.activate();

          let data = [[labels.masterSheetNoteText]];

          let range = masterSheet.getRange("A1:J1");
          range.merge(true);
          await ExcelHelper.tryCatchContextSync(context, `now merging ${masterSheetName}`);
          range = masterSheet.getRange("A1");
          range.values = data;
          range.format.font.bold = true;
          range.format.font.color = "red";

          await ExcelHelper.tryCatchContextSync(context, `loaded master worksheet ${masterSheetName}`);
          await ExcelUtilityHelper.ExcelSheetFormatting(
            context,
            masterSheetName,
            ExcelFormatting.passwordUnprotect,
            rowsDataModel.engagementId
          );

          let updateToCanvasRangeAddress: masterDataMetaDataValues =
            await ExcelUtilityHelper.populateUpdateToCanvasRange(rowsDataModel, masterSheet, context, masterSheetName);

          if (configuration?.uiExcelInterface?.populateReferenceSheetAsync) {
            // call this function when you need to populate additional data in referencesheet and return binding information
            additionalReferenceSheetBindingInformation =
              await configuration?.uiExcelInterface?.populateReferenceSheetAsync(
                rowsDataModel,
                masterSheet,
                context,
                masterSheetName,
                configuration
              );
          }

          //Now here again unmerge the cell.
          range = masterSheet.getRange("A1");
          range.unmerge();

          await ExcelHelper.tryCatchContextSync(context, `loaded master sheet with additional ref data`);

          await ExcelUtilityHelper.ExcelSheetFormatting(
            context,
            masterSheetName,
            ExcelFormatting.passwordProtect,
            rowsDataModel.engagementId,
            {}
          );
          await ExcelHelper.tryCatchContextSync(context, `protect & hide the sheet ${masterSheetName}`);

          masterDataAddressRanges = {
            YesNoList: updateToCanvasRangeAddress,
          };
        }
        //currentSheet.activate();
        await ExcelHelper.tryCatchContextSync(context, `activate the sheet to for request update ${masterSheetName}`);

        await this.deleteInstructionSheet();
        if (configuration.entityConfiguration?.instructionSheetConfigurations) {
          await this.addNewInstructionSheet(activeSheetId);
          const sheets = context.workbook.worksheets;
          const instructiontSheet = sheets.getItem(Constants.InstructionsSheetName);

          // call this function when you need to populate additional data in instruction sheet and return binding information
          additionalInstructionSheetBindingInformation =
            await configuration?.uiExcelInterface?.populateInstructionSheetAsync(
              rowsDataModel,
              instructiontSheet,
              context,
              instructionSheetName,
              configuration
            );

          const instructionSheetProtectionOptions = {
            allowFormatColumns: true,
            allowFormatRows: true,
          };
          await ExcelUtilityHelper.ExcelSheetFormatting(
            context,
            instructionSheetName,
            ExcelFormatting.passwordProtect,
            rowsDataModel.engagementId,
            instructionSheetProtectionOptions
          );
        }

        await ExcelHelper.tryCatchContextSync(context, `loaded instruction sheet with additional ref data`);

        masterSheet.visibility = Excel.SheetVisibility.hidden;

        await ExcelHelper.tryCatchContextSync(context, `protect & hide the sheet ${instructionSheetName}`);
      });
    }

    return [masterDataAddressRanges, additionalReferenceSheetBindingInformation];
  }

  /**
   * Returns a meta data object which keeps information of the columns/ranges which are used to determine Yes/No value cells
   * and which cells ranges are editable
   * @param requestsModel - The data model - Documents or Tasks (currently has documents property which needs to be removed.)
   * @param masterSheet - The master worksheet
   * @param context - The context
   * @param masterSheetName - the sheet name
   * @returns a Promise
   */
  public static async populateUpdateToCanvasRange(
    requestsModel: ServiceDataModel,
    masterSheet: Excel.Worksheet,
    context: Excel.RequestContext,
    masterSheetName: string
  ): Promise<{
    keyColumn: string;
    keyAddressRange: string;
    value1Column: string;
    value1AddressRange: string;
    value2Column: string;
    value2AddressRange: string;
  }> {
    const noOfItems: number = requestsModel.masterData.yesNoList.values
      ? requestsModel.masterData.yesNoList.values.length
      : 0;
    let yesNoRange: Excel.Range = masterSheet.getRange(`A4:A${3 + noOfItems}`);
    if (requestsModel.masterData.yesNoList.values) {
      yesNoRange.values = requestsModel.masterData.yesNoList.values;
    }
    await ExcelHelper.tryCatchContextSync(context, `populated the sign-off list ${masterSheetName}`);
    return {
      keyColumn: "A",
      keyAddressRange: `A4:A${5}`,
      value1Column: "A",
      value1AddressRange: `A4:A${5}`,
      value2Column: "",
      value2AddressRange: "",
    };
  }

  //To get current active sheet Id from the worksheet.
  public static async getActiveSheetIdFromWorksheet(): Promise<string> {
    let activeSheetId: string = "";
    await Excel.run(async (context) => {
      context.runtime.enableEvents = false;
      let currentSheet: Excel.Worksheet;
      currentSheet = context.workbook.worksheets.getActiveWorksheet();
      currentSheet.load("name");
      currentSheet.load("id");
      await ExcelHelper.tryCatchContextSync(context, `load the sheet name to text box`);
      activeSheetId = currentSheet.id;
    });

    return activeSheetId;
  }

  public static addNewInstructionSheet = async (activeSheetId) => {
    const instructionsSheetName = Constants.InstructionsSheetName;
    await Excel.run(async (context) => {
      let instructionSheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(instructionsSheetName);
      const inputSheet = context.workbook.worksheets.getItem(activeSheetId);
      inputSheet.load("position");
      await context.sync();

      instructionSheet = context.workbook.worksheets.add(instructionsSheetName);
      // Instruction sheet will take position of input sheet and reference sheet will be shifted to right by one position.
      instructionSheet.position = inputSheet.position;
      // Activate the input sheet to ensure it remains in focus
      inputSheet.activate();
      await ExcelHelper.tryCatchContextSync(
        context,
        `created instruction worksheet ${Constants.InstructionsSheetName}`
      );
      //instructionSheet.activate();
      await ExcelHelper.tryCatchContextSync(context, `loaded instruction worksheet ${Constants.InstructionsSheetName}`);
    });
  };

  public static deleteInstructionSheet = async () => {
    const instructionsSheetName = Constants.InstructionsSheetName;
    await Excel.run(async (context) => {
      let instructionSheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(instructionsSheetName);
      await ExcelHelper.tryCatchContextSync(context, `load the sheet ${instructionsSheetName}`);
      if (!instructionSheet.isNullObject) {
        instructionSheet.delete();
        await ExcelHelper.tryCatchContextSync(context, `delete existing worksheet ${instructionsSheetName}`);
      }
    });
  };

  public static getRowValue = async (
    context: Excel.RequestContext,
    sheet: Excel.Worksheet,
    rowIndex: number,
    columnCellName: string
  ): Promise<string> => {
    const docIdColumnRange: Excel.Range = sheet.getRange(`${columnCellName}${rowIndex}:${columnCellName}${rowIndex}`);
    docIdColumnRange.load("values");
    await context.sync();
    await ExcelHelper.tryCatchContextSync(context, `load for values`);
    return docIdColumnRange.values[0][0];
  };

  public static getCellValue = async (
    context: Excel.RequestContext,
    sheet: Excel.Worksheet,
    columnCellName: string
  ): Promise<string> => {
    const columnRange: Excel.Range = sheet.getRange(columnCellName);
    columnRange.load("values");
    await context.sync();
    await ExcelHelper.tryCatchContextSync(context, `load for values`);
    return columnRange.values[0][0];
  };

  public static addNewInputSheet = async (setBoundSheet, activeSheetId, engagementId) => {
    let sheetName: string = "";
    let activeSheetIdCreated: string = "";
    let errorMessage = "";
    const newBoundSheetName = `${Constants.NewBoundSheetName}${engagementId}`;
    try {
      await Excel.run(async (context) => {
        context.runtime.load("enableEvents");
        await context.sync();
        context.runtime.enableEvents = false;
        await context.sync();
        let newBoundSheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(newBoundSheetName);
        newBoundSheet = context.workbook.worksheets.add(newBoundSheetName);
        await ExcelHelper.tryCatchContextSync(context, `created master worksheet ${newBoundSheetName}`);
        newBoundSheet.load("name");
        newBoundSheet.load("id");
        await ExcelHelper.tryCatchContextSync(context, `loaded master worksheet ${newBoundSheetName}`);
        sheetName = newBoundSheet.name;
        activeSheetIdCreated = newBoundSheet.id;

        await ExcelHelper.tryCatchContextSync(context, `created master worksheet ${newBoundSheetName}`);
        newBoundSheet.activate();
        await ExcelHelper.tryCatchContextSync(context, `created master worksheet ${newBoundSheetName}`);
        //newBoundSheet.visibility = Excel.SheetVisibility.hidden;
      });
    } catch (e) {
      errorMessage = ExcelUtilityHelper.excelErrorMessageMapping(e.code);
    } finally {
      setBoundSheet(sheetName);
      activeSheetId.current = activeSheetIdCreated;
      return errorMessage;
    }
  };

  public static checkForExistingSheet = async (engagementId) => {
    const newBoundSheetName = `${Constants.NewBoundSheetName}${engagementId}`;
    let sheetWithSameNameExist: string = null;
    await Excel.run(async (context) => {
      let masterSheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(newBoundSheetName);
      await ExcelHelper.tryCatchContextSync(context, `load the sheet ${newBoundSheetName}`);
      if (!masterSheet.isNullObject) {
        sheetWithSameNameExist = engagementId;
      }
    });
    return sheetWithSameNameExist;
  };

  public static validateUserInput = (
    uploadStatusColumnRange: Excel.Range,
    uploadStatusIdColumnRange: Excel.Range,
    uploadedRowRange: Excel.Range,
    markCellRange: Excel.Range,
    exceptionMessage: string,
    markForUpdate: string,
    color: string
  ) => {
    uploadStatusColumnRange.format.protection.locked = false;
    uploadStatusIdColumnRange.format.protection.locked = false;
    uploadStatusColumnRange.values = [[exceptionMessage]];
    uploadStatusIdColumnRange.values = [[exceptionMessage]];
    uploadStatusColumnRange.format.protection.locked = true;
    uploadStatusIdColumnRange.format.protection.locked = true;
    markCellRange.values = [[markForUpdate]];
    uploadedRowRange.format.fill.color = color;
  };

  public static excelErrorMessageMapping = (errorCode: Excel.ErrorCodes, loadExisting = false) => {
    switch (errorCode) {
      case "InvalidOperationInCellEditMode": //When user clicks on excel while addin is loading for popluating or while doing offline edit process.
        return !loadExisting ? labels.excelRenderIssue : labels.offlineModeUserClickError;
      case "InactiveWorkbook": //When workbook is inactive state that is workbook is minimized or workbook in background and user tries to do some operation.
        return !loadExisting ? labels.inActiveWorkbookError : "";
      default:
        return labels.genericErrorMessage;
    }
  };

  //To protect and unprotect the excel sheet with password.
  public static ExcelSheetFormatting = async (
    context: Excel.RequestContext,
    sheetNameorId: string,
    formattingType: any,
    engagementId: string,
    protectionOptions: object = {}
  ) => {
    let activeSheet = context.workbook.worksheets.getItem(sheetNameorId);
    activeSheet.load("protection/protected");
    await ExcelHelper.tryCatchContextSync(context, `load the protection properties ${sheetNameorId}`);

    const sheetPassword = ExcelUtilityHelper.getSheetProtectionPassword(engagementId);

    switch (formattingType) {
      case ExcelFormatting.passwordProtect:
        if (!activeSheet.protection.protected) {
          activeSheet.protection.protect(protectionOptions, sheetPassword);
        }
        break;
      case ExcelFormatting.passwordUnprotect:
        if (activeSheet.protection.protected) {
          activeSheet.protection.unprotect(sheetPassword);
        }
        break;
      default:
        break;
    }
  };

  /**
   * Builds the worksheet protection password instead of shipping a hardcoded one.
   * The pattern is deliberately reproducible, so support can rebuild it for a workbook from the
   * user's universal gui and the engagement the sheet was created for.
   */
  public static getSheetProtectionPassword = (engagementId: string): string => {
    return `${Environment.getCurrentUserGui()}_${engagementId}`;
  };

  public static drawBorderForCell(cell: Excel.Range, borderStyle, borderColor) {
    let excelEdge: ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight"] = [
      "EdgeTop",
      "EdgeBottom",
      "EdgeLeft",
      "EdgeRight",
    ];

    excelEdge.forEach((edge) => {
      if (borderStyle) cell.format.borders.getItem(edge).style = borderStyle;
      if (borderColor) cell.format.borders.getItem(edge).color = borderColor;
    });
  }

  public static async recalculateHashvalue(
    updatedRowIndex: number,
    sheet: Excel.Worksheet,
    context: Excel.RequestContext,
    configuration: EntityConfiguration
  ) {
    let dataToUpdateBackToCanvas = Utility.getHashValueColumnsToUpdate(configuration.columns, []); // can be more then one
    let editableFieldRanges: Array<any> = new Array<any>();
    let hashColumn = configuration.columns.find((cl) => cl.CellId == ColumnType.HashValue); // can be one
    let hashCellName = "";
    if (hashColumn) {
      hashCellName = CellIndexMapping[hashColumn.CellDisplayIndex];
    }
    let objectCell = {};
    dataToUpdateBackToCanvas.forEach((column) => {
      const cellName = CellIndexMapping[column.CellDisplayIndex];
      let updatedData = sheet.getRange(`${cellName}${updatedRowIndex}:${cellName}${updatedRowIndex}`);
      updatedData.load("text");
      updatedData.load("rowIndex");
      objectCell[column.CellId] = updatedData;
    });

    if (hashColumn) {
      hashCellName = CellIndexMapping[hashColumn.CellDisplayIndex];
      let hashValueCellRange = sheet.getRange(`${hashCellName}${updatedRowIndex}:${hashCellName}${updatedRowIndex}`);
      hashValueCellRange.load("values");
      hashValueCellRange.load("text");
      hashValueCellRange.load("address");
      hashValueCellRange.load("rowIndex");
      objectCell[hashColumn.CellId] = hashValueCellRange;
    }

    editableFieldRanges.push(objectCell);

    // for performance reasons first load all the text, then perform updates
    await ExcelHelper.tryCatchContextSync(context, `load selected edit cell ranges for hash update`);
    _.forEach(editableFieldRanges, (editableFieldRange) => {
      let updatedData = "";
      dataToUpdateBackToCanvas.forEach((column) => {
        updatedData += editableFieldRange[column.CellId].text.at(0)[0];
      });

      const newHash: string = hash(updatedData);

      let hashValueCellRange = editableFieldRange?.HashValue;
      if (hashValueCellRange) {
        hashValueCellRange.format.protection.locked = false;
        hashValueCellRange.values = [[newHash]];
        hashValueCellRange.format.protection.locked = true;
      }
    });
  }
}
